import React from 'react';
import { Card, Typography, Tag, Alert } from 'antd';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import parse from 'html-react-parser';
import { 
  TEXT_TYPES, 
  detectTextType, 
  sanitizeHTML, 
  formatJSON, 
  formatPlainText,
  validateText,
  getTextTypeName
} from '../utils/textParser';

const { Text } = Typography;

const TextPreview = ({ 
  content, 
  title = '预览', 
  showTypeTag = true, 
  maxHeight = 400,
  style = {} 
}) => {
  if (!content) {
    return (
      <Card title={title} style={style}>
        <Text type="secondary">暂无内容</Text>
      </Card>
    );
  }

  const textType = detectTextType(content);
  const validation = validateText(content, textType);

  const renderContent = () => {
    if (!validation.isValid) {
      return (
        <Alert
          message="内容格式错误"
          description={validation.error}
          type="error"
          showIcon
        />
      );
    }

    switch (textType) {
      case TEXT_TYPES.MARKDOWN:
        return (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              code({ node, inline, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || '');
                return !inline && match ? (
                  <SyntaxHighlighter
                    style={tomorrow}
                    language={match[1]}
                    PreTag="div"
                    {...props}
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              },
              a: ({ href, children }) => (
                <a href={href} target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              ),
              img: ({ src, alt }) => (
                <img 
                  src={src} 
                  alt={alt} 
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
              )
            }}
          >
            {content}
          </ReactMarkdown>
        );

      case TEXT_TYPES.HTML:
        const sanitizedHTML = sanitizeHTML(content);
        return (
          <div>
            {validation.warning && (
              <Alert
                message="HTML格式警告"
                description={validation.error}
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            <div>{parse(sanitizedHTML)}</div>
          </div>
        );

      case TEXT_TYPES.JSON:
        const formattedJSON = formatJSON(content);
        return (
          <SyntaxHighlighter
            language="json"
            style={tomorrow}
            customStyle={{
              margin: 0,
              borderRadius: 4
            }}
          >
            {formattedJSON}
          </SyntaxHighlighter>
        );

      case TEXT_TYPES.PLAIN:
      default:
        const formattedText = formatPlainText(content);
        return (
          <div 
            dangerouslySetInnerHTML={{ __html: formattedText }}
            style={{ 
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.6
            }}
          />
        );
    }
  };

  // 只有当有标题或需要显示类型标签时才显示 title
  const shouldShowTitle = title || showTypeTag;
  
  return (
    <Card 
      title={shouldShowTitle ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{title}</span>
          {showTypeTag && (
            <Tag color={getTypeColor(textType)}>
              {getTextTypeName(textType)}
            </Tag>
          )}
        </div>
      ) : undefined}
      style={style}
      bodyStyle={{
        maxHeight: maxHeight,
        overflowY: 'auto',
        padding: '16px'
      }}
    >
      {renderContent()}
    </Card>
  );
};

// 获取文本类型对应的颜色
const getTypeColor = (type) => {
  const colorMap = {
    [TEXT_TYPES.PLAIN]: 'default',
    [TEXT_TYPES.MARKDOWN]: 'blue',
    [TEXT_TYPES.HTML]: 'green',
    [TEXT_TYPES.JSON]: 'orange'
  };
  return colorMap[type] || 'default';
};

export default TextPreview; 